// 세리사무소(税理士事務所) 전용 홈 — 포털 본문.
//
// 이름이 zeirishi 인 이유: 税理士事務所(세무 대리 업무를 하는 사무소)이지 税務署(과세 관청)가
// 아니다. taxoffice 로 두면 영어권 개발자가 후자로 읽을 여지가 있어, 직업 자격을 가리키는
// 말을 그대로 쓴다.
//
// 화면 구성은 시안(세무대리인포털_메인_v8.html)을 따른다 — 브리핑 3칸 → 오늘의 업무 →
// 수익 기회. 상단바·우측 레일·토스트는 껍데기(Layout.zeirishi)가 그리고, 이 파일은 본문만
// 그린다. 상태는 컨텍스트(Home.zeirishi.context)에 있다: 브리핑 칸이 여는 업무를 사이드바의
// 업무 이력도 봐야 하고, 검색바의 질문이 사이드바의 AI 대화로 들어가야 하기 때문이다.
//
// 데이터는 Home.zeirishi.data.ts 의 목업이다. 실제로 동작하는 것은 AI 대화 하나뿐이고,
// 그것만 게이트웨이(chat-api·embed-api)를 호출한다 — 연출이 아니다.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import ZeirishiWork from './Home.zeirishi.work';
import ZeirishiRevenue from './Home.zeirishi.revenue';
import { useZeirishi } from './Home.zeirishi.context';
import { AUTO_TOTAL, WORK_ITEMS } from './Home.zeirishi.data';
import styles from './Home.zeirishi.main.module.css';

/** 브리핑 3칸 — 칸마다 목적지가 있고, 누르면 아래 업무·수익으로 데려간다 */
type BriefGo = 'review' | 'due' | 'rev';

export default function HomeZeirishi() {
  const { queue, toast, askAi } = useZeirishi();

  const rootRef = useRef<HTMLDivElement>(null);
  const workRef = useRef<HTMLDivElement>(null);
  const revRef = useRef<HTMLDivElement>(null);

  const [draft, setDraft] = useState('');

  const scrollTo = useCallback((ref: React.RefObject<HTMLDivElement>) => {
    const el = ref.current;
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const briefGo = useCallback((kind: BriefGo) => {
    if (kind === 'rev') {
      scrollTo(revRef);
      return;
    }
    // 마감 임박은 그 건을, 확인 필요는 남은 첫 건을 연다
    const target = kind === 'due' ? WORK_ITEMS[0] : queue.liveItems[0];
    if (target) {
      queue.focus(target.id);
    }
    scrollTo(workRef);
  }, [queue, scrollTo]);

  /* ── 키보드 ──
     Enter 로 승인이 이어지는 것이 이 화면의 핵심 동작이다(기획서 6.4).
     다만 모듈 화면으로 이동해도 이 홈은 마운트된 채 숨어 있을 뿐이라, 보이지 않을 때
     키를 먹으면 안 된다 — display:none 이면 offsetParent 가 null 인 것으로 판별한다. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root || root.offsetParent === null) {
        return;
      }
      // target 이 document 일 수 있다 — tagName 을 바로 읽으면 예외가 나 핸들러가 통째로 죽는다
      const el = e.target as HTMLElement | null;
      const tag = el && el.tagName ? el.tagName.toUpperCase() : '';
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.metaKey || e.ctrlKey) {
        return;
      }
      if (e.key === 'Enter' && queue.selectedId && queue.doneIds.indexOf(queue.selectedId) < 0) {
        e.preventDefault();
        queue.approve();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [queue]);

  const ask = () => {
    askAi(draft);
    setDraft('');
  };

  const remain = queue.liveItems.length;

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.scroll}>
        <div className={styles.center}>
          <h1 className={styles.pname}>세무법인 정담 <span className={styles.accent}>Works</span></h1>
          <p className={styles.ptag}>
            ✦ 밤사이 AI가 <b>{AUTO_TOTAL}건</b>을 끝냈습니다. {remain
              ? <><b>{remain}건</b>만 확인해 주세요.</>
              : <>확인할 업무는 남지 않았습니다.</>}
          </p>
        </div>

        {/* 묻기(질문)와 실행(지시)을 함께 받는 자리 — 사이드바의 AI 대화로 들어간다 */}
        <div className={styles.searchWrap}>
          <div className={styles.search}>
            <svg className={styles.sIco} viewBox="0 0 24 24" fill="none" stroke="#7a8fa8" strokeWidth="2.2" strokeLinecap="round">
              <circle cx="11" cy="11" r="7" /><path d="m20.5 20.5-3.6-3.6" />
            </svg>
            <input
              value={draft}
              placeholder="궁금한 점을 물어보거나 원하는 작업을 시작하세요."
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask();
                }
              }}
            />
            <button type="button" className={styles.oneai} onClick={ask}>✦ AI</button>
          </div>
        </div>

        {/* ── ProActive AI 브리핑 — 아침에 3초 안에 읽는 요약 ── */}
        <div className={styles.brief3}>
          <div className={styles.aisum}>
            <div className={styles.ah}>
              <span className={styles.ahB}>✦ ProActive AI</span>
              <span className={styles.ahT}>오늘 아침 브리핑</span>
              <span className={styles.ahS}>2026. 9. 16 (수) 08:00 기준</span>
            </div>
            <div className={styles.items}>
              <div className={styles.it} onClick={() => briefGo('review')}>
                <span className={`${styles.pri} ${styles.priWarn}`}>확인 필요</span>
                <span className={styles.gg}>내용 보기 →</span>
                <div className={styles.tt}>AI가 판단을 유보한 {remain}건</div>
                <div className={styles.ds}>
                  급여 연차수당, 일용직 비과세 한도, 이상 분개, <b>우리 사무소 중간예납</b> 등.
                  <b> 평균 40초</b>면 한 건을 처리합니다.
                </div>
              </div>
              <div className={styles.it} onClick={() => briefGo('due')}>
                <span className={`${styles.pri} ${styles.priUrgent}`}>마감 임박</span>
                <span className={styles.gg}>내용 보기 →</span>
                <div className={styles.tt}>부가세 예정신고 D-2</div>
                <div className={styles.ds}>
                  <b>(주)한빛테크</b> 외 3개사 신고서 초안 완료. 불공제 판단 <b>3건</b>만 확인하면
                  전자신고로 넘어갑니다.
                </div>
              </div>
              <div className={styles.it} onClick={() => briefGo('rev')}>
                <span className={`${styles.pri} ${styles.priGood}`}>수익 기회</span>
                <span className={styles.gg}>내용 보기 →</span>
                <div className={styles.tt}>매칭된 제안 4건 · 522만원</div>
                <div className={styles.ds}>
                  기장 데이터 기준 정책자금·대출·팩토링 <b>매칭 완료</b>. 제안서는 이미 작성되어 있습니다.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div ref={workRef}>
          <ZeirishiWork queue={queue} toast={toast} onGoRevenue={() => scrollTo(revRef)} />
        </div>

        <div ref={revRef}>
          <ZeirishiRevenue queue={queue} onOpenInPanel={() => scrollTo(workRef)} />
        </div>
      </div>
    </div>
  );
}
